import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { CopyField } from '../../components/CopyField.js';
import { BINGO_BATCH_MAX, BINGO_NICKNAME_MAX, BINGO_NICKNAME_MIN, type BingoHeldCodeView, type BingoPlayerView } from '../../../shared/bingo.js';

// Bingo players and the coupon limit (decisions 2026-10-01): an own limit for a single player (the
// general limit per game, default 3, is set in "Koodien jakelu" since 2026-10-07). The tab "Koodit ja pelaajat" (user request
// 2026-10-02): every code a player holds (reserved or claimed), releasing a code (a new code takes its
// place, free or reserved for another player) and changing a nickname. Removing a player or all of them
// (user request 2026-10-07): their codes are voided and new free codes take their place.

type Flash = { ok: boolean; text: string } | null;
type View = { codes: BingoHeldCodeView[]; players: BingoPlayerView[]; max: number };
const p = () => t.admin.bingoPlayers;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const when = (ms: number | null) => (ms === null ? null : new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' }));
const spaced = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`;

export function BingoPlayers(props: { onFlash: (f: Flash) => void }) {
  const [data, setData] = useState<View | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<{ player: BingoPlayerView; nickname: string } | null>(null);
  const [releasing, setReleasing] = useState<{ code: BingoHeldCodeView; to: string } | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [removing, setRemoving] = useState<BingoPlayerView | 'all' | null>(null);
  const apply = (d: Partial<View> & { players: BingoPlayerView[]; max: number }) => {
    setData((old) => ({ codes: d.codes ?? old?.codes ?? [], players: d.players, max: d.max }));
  };
  useEffect(() => {
    api<View>('GET', '/api/admin/bingo/codes')
      .then(apply)
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  }, []);
  const run = async <T extends Partial<View> & { players: BingoPlayerView[]; max: number }>(fn: () => Promise<T>, ok: (r: T) => string) => {
    setBusy(true);
    try {
      const r = await fn();
      apply(r);
      props.onFlash({ ok: true, text: ok(r) });
      return r;
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
      return null;
    } finally {
      setBusy(false);
    }
  };
  const saveOwn = (player: BingoPlayerView, value: string) => {
    const own = value.trim() === '' ? null : Number(value);
    if (own !== null && (!Number.isInteger(own) || own < 1 || own > BINGO_BATCH_MAX)) return;
    if (own === player.maxCoupons) return;
    void run(() => api<View>('PATCH', `/api/admin/bingo/players/${player.id}`, { maxCoupons: own }), () => p().ownSaved(player.nickname));
  };
  const rename = () => {
    if (!renaming) return;
    const { player, nickname } = renaming;
    setRenaming(null);
    void run(() => api<View>('PUT', `/api/admin/bingo/players/${player.id}/nickname`, { nickname: nickname.trim() }), () => p().renamed(nickname.trim()));
  };
  const release = () => {
    if (!releasing) return;
    const { code, to } = releasing;
    setReleasing(null);
    void run(
      () => api<View & { code: string }>('POST', '/api/admin/bingo/codes/release', { code: code.code, toPlayerId: to ? Number(to) : null }),
      (r) => p().released(spaced(r.code)),
    ).then((r) => setFresh(r ? r.code : null));
  };

  const remove = () => {
    if (!removing) return;
    const target = removing;
    setRemoving(null);
    if (target === 'all') {
      void run(
        () => api<View & { removed: number; kept: number }>('DELETE', '/api/admin/bingo/players'),
        (r) => p().removedAll(r.removed, r.kept),
      );
    } else {
      void run(() => api<View>('DELETE', `/api/admin/bingo/players/${target.id}`), () => p().removed(target.nickname));
    }
  };

  const needle = filter.trim().toLowerCase();
  const players = (data?.players ?? []).filter((x) => x.nickname.toLowerCase().includes(needle));
  const codes = (data?.codes ?? []).filter((x) => x.nickname.toLowerCase().includes(needle) || x.code.toLowerCase().includes(needle.replace(/\s/g, '')));
  return (
    <>
      <section class="card form">
        <h2>{p().title}</h2>
        <p class="hint">{p().info}</p>
        {/* The general limit is set with the other settings of handing out the codes (2026-10-07). */}
        {data && <p class="muted">{p().maxElsewhere(data.max)}</p>}
        {!data ? (
          <p class="muted">{t.admin.autoBingo.loading}</p>
        ) : data.players.length === 0 ? (
          <p class="muted">{p().none}</p>
        ) : (
          <>
            <label>
              {p().search}
              <input value={filter} onInput={(e) => setFilter(e.currentTarget.value)} />
            </label>
            <div class="bingo-start-actions">
              <button class="btn btn-danger" type="button" disabled={busy} onClick={() => setRemoving('all')}>
                {p().removeAll(data.players.length)}
              </button>
            </div>
            <ul class="user-list bingo-player-list">
              {players.map((x) => (
                <li class="user-row" key={x.id}>
                  <div class="user-main">
                    <div class="user-name">{x.nickname}</div>
                    <div class="user-sub">{p().counts(x.upcoming, x.coupons, x.reserved)}</div>
                    <div class="user-sub">{p().lastSeen(when(x.lastSeenAt))}</div>
                  </div>
                  <label class="bingo-player-limit">
                    {p().own}
                    <input
                      type="number"
                      min={1}
                      max={BINGO_BATCH_MAX}
                      placeholder={String(data.max)}
                      value={x.maxCoupons ?? ''}
                      disabled={busy}
                      onBlur={(e) => saveOwn(x, e.currentTarget.value)}
                    />
                  </label>
                  <button class="btn btn-small" type="button" disabled={busy} onClick={() => setRenaming({ player: x, nickname: x.nickname })}>
                    {p().rename}
                  </button>
                  <button class="btn btn-small btn-danger" type="button" disabled={busy} onClick={() => setRemoving(x)}>
                    {p().remove}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section class="card form">
        <h2>{p().codesTitle(data?.codes.length ?? 0)}</h2>
        <p class="hint">{p().codesInfo}</p>
        {fresh && <CopyField value={fresh} label={p().released(spaced(fresh))} />}
        {data && data.codes.length === 0 ? (
          <p class="muted">{p().codesNone}</p>
        ) : (
          <div class="sms-table-wrap">
            <table class="sms-table">
              <thead>
                <tr>
                  <th>{t.admin.sms.colCode}</th>
                  <th>{t.admin.sms.colName}</th>
                  <th>{t.admin.sms.batchLabel}</th>
                  <th>{t.admin.sms.colState}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code}>
                    <td>
                      <code>{spaced(c.code)}</code>
                    </td>
                    <td>
                      {c.nickname}
                      <div class="muted">{p().lastSeen(when(c.lastSeenAt))}</div>
                    </td>
                    <td>
                      {c.batchLabel}
                      <div class="muted">{c.claimingOpen ? p().claimingOpen : p().claimingClosed}</div>
                    </td>
                    <td>{c.cardNumber === null ? p().codeReserved : p().codeCard(c.cardNumber)}</td>
                    <td>
                      <button class="btn btn-small btn-danger" type="button" disabled={busy} onClick={() => setReleasing({ code: c, to: '' })}>
                        {p().release}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {renaming && (
        <ConfirmDialog
          title={p().renameTitle(renaming.player.nickname)}
          confirmLabel={p().rename}
          cancelLabel={t.admin.cancel}
          confirmDisabled={renaming.nickname.trim().length < BINGO_NICKNAME_MIN || renaming.nickname.trim() === renaming.player.nickname}
          onConfirm={rename}
          onCancel={() => setRenaming(null)}
        >
          <label>
            {p().renameLabel}
            <input
              value={renaming.nickname}
              minLength={BINGO_NICKNAME_MIN}
              maxLength={BINGO_NICKNAME_MAX}
              onInput={(e) => setRenaming({ ...renaming, nickname: e.currentTarget.value })}
            />
          </label>
        </ConfirmDialog>
      )}
      {removing && (
        <ConfirmDialog
          title={removing === 'all' ? p().removeAllTitle(data?.players.length ?? 0) : p().removeTitle(removing.nickname)}
          confirmLabel={removing === 'all' ? p().removeAll(data?.players.length ?? 0) : p().remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={remove}
          onCancel={() => setRemoving(null)}
        >
          <p>{p().removeText}</p>
        </ConfirmDialog>
      )}
      {releasing && (
        <ConfirmDialog
          title={p().releaseTitle(spaced(releasing.code.code), releasing.code.nickname)}
          confirmLabel={p().releaseConfirm}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={release}
          onCancel={() => setReleasing(null)}
        >
          <p>{p().releaseText}</p>
          <label>
            {p().releaseTo}
            <select value={releasing.to} onChange={(e) => setReleasing({ ...releasing, to: e.currentTarget.value })}>
              <option value="">{p().releaseFree}</option>
              {(data?.players ?? [])
                .filter((x) => x.id !== releasing.code.playerId)
                .map((x) => (
                  <option key={x.id} value={String(x.id)}>
                    {x.nickname}
                  </option>
                ))}
            </select>
          </label>
        </ConfirmDialog>
      )}
    </>
  );
}
